import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, ElementRef, inject, Injector, NgZone, signal, ViewEncapsulation } from '@angular/core';
import { ActivityEntityName, DateUtility, MetadataEntityFieldData, MetadataEntityFieldType, MetadataEnumTypeData, UnallocatedUserLabel, isUnallocatedUser } from '@jms/model';
import { GridApi, IRowNode } from 'ag-grid-enterprise';
import { AvatarMode } from '@workbuddy/ui/components/avatar';
import { BadgeComponent, BadgeMode } from '@workbuddy/ui/components/badge';
import { HoverCardComponent, type HoverCardRow } from '@workbuddy/ui/v2/components/hover.card';
import { ContentComponent, ContentModel, ContentToken } from '@workbuddy/ui/v2/components/primitives/content';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { WbValueComponent, WbValueCompletionComponent } from '@workbuddy/ui/v2/components/primitives/value';
import { timezoneForDisplayedDate, timezoneForFieldType } from '@workbuddy/ui/v2/components/primitives/value/wall.clock.date.timezone';
import { CheckboxComponent } from '@workbuddy/ui/v2/fields/checkbox';
import { DateTimeFormatPipe } from '@workbuddy/ui/fields/luxon.calendar';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { TooltipModule } from 'primeng/tooltip';
import { BaseCellRenderer } from '../../../cell.renderers';
import { EntityBoardListNestedConfig, type EntityBoardListParentProgress, type EntityBoardListPropertyHoverCard, type EntityBoardListPropertyIconBadge, type EntityBoardListPropertyPresentation, type EntityBoardListTitleAdornment, getNestedListItemDepth, getNestedListParentValue, orderListDisplayFields } from '../../../context';
import { EntityBoardStore } from '../../../state';
import type { ListRowCellParams } from '../list.row.cell.renderer';
import { resolveListRowLabelSummary } from '../list.row.label.summary';
import { listRowPropertyToneStyle } from '../list.row.property.tone';
import { foldListRowProperties, type ListRowV2FoldItem } from './list.row.v2.fold';
import { listRowV2BridgeStyles, listRowV2StyleId } from './list.row.v2.theme';

// One-line list row (WBS-13010), a copy of ListRowCellRenderer kept apart so it can be reverted by
// dropping `rowRenderer` from a manifest. It never wraps: the title ellipsizes down to a floor, then
// properties hide whole behind "+N" in LIST_ROW_V2_FOLD_ORDER; pinned properties and the assignee
// always stay. Unlike v1 it never overlaps chips and ignores board density.

type FieldEntry = {
  badge?: RowProperty;
  field: MetadataEntityFieldData;
  value: any;
  truncate: boolean;
  title: boolean;
  /** Full title text for the hover once the title ellipsizes. */
  text: string | null;
};

type RowProperty = {
  id: string;
  field: MetadataEntityFieldData;
  mode: BadgeMode;
  label: string;
  tooltip: string;
  iconLeft?: string;
  iconLeftColor?: string;
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  dashed?: boolean;
  disabled?: boolean;
  dotColors?: string[];
  hoverCard?: EntityBoardListPropertyHoverCard;
  iconOnly?: boolean;
  iconBadge?: EntityBoardListPropertyIconBadge;
  /** Listed in the board's fixedProperties: never folds. */
  pinned: boolean;
  /** Created/updated: plain trailing date text rather than a chip. */
  meta: boolean;
  /** Its line in the "+N" hover card while folded. */
  foldRow: HoverCardRow;
};

type AssigneeSlot = {
  enabled: boolean;
  tooltip: string | null;
};

// Matches the rendered "+N" pill so the fold reserves its room before it shows.
const OVERFLOW_PILL_WIDTH = 36;

function sameProperties(current: RowProperty[], next: RowProperty[]): boolean {

  return current.length === next.length && current.every((item, index) => JSON.stringify(item) === JSON.stringify(next[index]));
}

function sameIds(current: ReadonlySet<string>, next: ReadonlySet<string>): boolean {

  return current.size === next.size && [...current].every(id => next.has(id));
}

/** Entity board list row for manifests that set `list.rowRenderer: ListRowV2Renderer`; task lists use it. */
@Component({
  selector: 'list-row-v2',
  standalone: true,
  imports: [NgTemplateOutlet, WbValueComponent, WbValueCompletionComponent, CheckboxComponent, ContentComponent, BadgeComponent, TooltipModule, HoverCardComponent],
  templateUrl: './list.row.v2.renderer.html',
  styleUrls: ['./list.row.v2.renderer.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.is-row-selected]': '$selected()', '[class.is-nested]': '$nested()', '[class.is-measuring]': '$measuring()', '[class.drag-blocked]': '$dragBlocked()', '(mousedown)': 'onMouseDown($event)' },
  providers: [DateTimeFormatPipe],
})
export class ListRowV2Renderer extends BaseCellRenderer<any, ListRowCellParams> {

  protected readonly BadgeMode = BadgeMode;
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly generalSettings = inject(GeneralSettingService);
  private readonly dateTimePipe = inject(DateTimeFormatPipe);
  /** Live board state — AG colDef cellRendererParams can go stale; always re-read fields here. */
  private readonly store = inject(EntityBoardStore, { optional: true });

  protected $data = signal<any>(null);
  protected $leftEntries = signal<FieldEntry[]>([]);
  protected $bulkSelectable = signal(false);
  protected $selected = signal(false);
  protected $nested = signal(false);
  protected $nestedLast = signal(true);
  protected $dragBlocked = signal(false);
  protected $titleAdornment = signal<EntityBoardListTitleAdornment | null>(null);
  protected $parentProgress = signal<EntityBoardListParentProgress | null>(null);
  protected $flatParentLabel = signal<string | null>(null);
  protected $properties = signal<RowProperty[]>([]);
  protected $avatarContent = signal<ContentModel | null>(null);
  protected $assigneeSlot = signal<AssigneeSlot>({ enabled: false, tooltip: null });
  /** Ids hidden behind "+N"; cleared while measuring so every width is real. */
  protected $folded = signal<ReadonlySet<string>>(new Set());
  protected $measuring = signal(false);
  protected readonly $chips = computed(() => this.$properties().filter(item => !item.pinned && !item.meta));
  protected readonly $pinned = computed(() => this.$properties().filter(item => item.pinned));
  protected readonly $metas = computed(() => this.$properties().filter(item => !item.pinned && item.meta));
  protected readonly $foldedRows = computed(() => this.$properties().filter(item => this.$folded().has(item.id)).map(item => item.foldRow));

  private static readonly PERSON_ENTITIES = new Set([ActivityEntityName.ContactEmployee, ActivityEntityName.Resource, ActivityEntityName.Contact, ActivityEntityName.Contractor, ActivityEntityName.InvoiceContact]);
  private static readonly CREATED_IDS = new Set(['createdAt', 'created']);
  private static readonly UPDATED_IDS = new Set(['updatedAt', 'updated']);
  private static readonly DUE_IDS = new Set(['dueDate', 'due']);
  private rowNode: IRowNode | null = null;
  private gridApi: GridApi | null = null;
  private columnId: string | null = null;
  private foldWidths: ListRowV2FoldItem[] = [];
  private pinnedWidth = 0;
  private layoutFrame: number | null = null;
  private measureQueued = false;
  private widthsStale = false;

  private readonly defaultTimezone = this.generalSettings.generalSettings.region.timezone ?? 'UTC';

  public constructor() {

    super();
    DesignSystem.Mount(this.document, listRowV2StyleId, listRowV2BridgeStyles);
    this.observeWidth();
  }

  protected hasPropertyAction(item: RowProperty): boolean {

    return !item.disabled && !!this.store?.$context()?.list?.propertyActions?.[item.id];
  }

  protected async onPropertyClick(event: Event, item: RowProperty): Promise<void> {

    if (!this.hasPropertyAction(item)) return;
    event.stopPropagation();
    await this.store?.$context()?.list?.propertyActions?.[item.id]?.({ item: this.$data(), field: item.field, injector: this.injector });
  }

  protected onPropertyKeydown(event: KeyboardEvent, item: RowProperty): void {

    if (!this.hasPropertyAction(item)) return;
    event.stopPropagation();
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      void this.onPropertyClick(event, item);
    }
  }

  public displayLogic(params: ListRowCellParams): void {

    if (params.node?.group && !params.data) {
      this.rowNode = null;
      this.gridApi = null;
      this.columnId = null;
      this.$data.set(null);
      this.$leftEntries.set([]);
      this.$bulkSelectable.set(false);
      this.$selected.set(false);
      this.$nested.set(false);
      this.$nestedLast.set(true);
      this.$dragBlocked.set(false);
      this.$titleAdornment.set(null);
      this.$parentProgress.set(null);
      this.$flatParentLabel.set(null);
      this.$properties.set([]);
      this.$avatarContent.set(null);
      this.$assigneeSlot.set({ enabled: false, tooltip: null });
      this.$folded.set(new Set());
      this.cdr.markForCheck();
      return;
    }

    const data = params.data;
    this.rowNode = params.node;
    this.gridApi = params.api;
    this.columnId = params.column?.getColId() ?? null;
    this.$data.set(data);
    this.$bulkSelectable.set(this.store?.$selection()?.type === 'multiple');
    this.$selected.set(!!params.node?.isSelected());
    this.$dragBlocked.set(!!params.canDrag && !params.canDrag(data));
    this.$titleAdornment.set(this.store?.$context()?.list?.titleAdornment?.(data) ?? null);
    this.$parentProgress.set(this.store?.$context()?.list?.parentProgress?.(data) ?? null);

    // Prefer live store fields over colDef params (params go stale after display-settings toggles).
    const { identifierField, titleField, statusField, rightFields } = this.resolveListFields(params);
    this.setNestedPresentation(data, titleField);

    const left: FieldEntry[] = [];
    if (statusField) {
      const value = this.getValueFromPath(data, statusField.path ?? statusField.name);
      if (value != null || statusField.visualType === 'completion') {
        left.push({ field: statusField, value, truncate: false, title: false, text: null });
      }
    }
    if (identifierField) {
      const value = this.getValueFromPath(data, identifierField.path ?? identifierField.name);
      const presentation = this.store?.$context()?.list?.identifierPresentation?.({ item: data, field: identifierField, injector: this.injector });
      if (value != null) left.push({ field: identifierField, value, truncate: false, title: false, text: null,
        ...(presentation ? { badge: this.toPresentedProperty(identifierField.path ?? identifierField.name, identifierField, presentation, false) } : {}) });
    }
    if (titleField) {
      const value = this.getValueFromPath(data, titleField.path ?? titleField.name);
      if (value != null) left.push({ field: titleField, value, truncate: true, title: true, text: this.titleText(value) });
    }
    this.$leftEntries.set(left);

    const partitioned = this.partitionRightFields(data, rightFields);
    this.$avatarContent.set(partitioned.avatarContent);
    this.$assigneeSlot.set(partitioned.assignee);
    if (!sameProperties(this.$properties(), partitioned.properties)) {
      this.$properties.set(partitioned.properties);
      this.measure();
    }
    this.cdr.markForCheck();
  }

  /** Resolve list field metadata from the board store (live) with colDef params as fallback. */
  private resolveListFields(params: ListRowCellParams): {
    identifierField: MetadataEntityFieldData | null;
    titleField: MetadataEntityFieldData | null;
    statusField: MetadataEntityFieldData | null;
    rightFields: MetadataEntityFieldData[];
  } {

    const store = this.store;
    if (!store) {
      return {
        identifierField: params.identifierField ?? null,
        titleField: params.titleField ?? null,
        statusField: params.statusField ?? null,
        rightFields: params.rightFields ?? [],
      };
    }

    const view = store.$effectiveView();
    const uiFields = (store.$uiFields?.() ?? []) as MetadataEntityFieldData[];
    const map = new Map<string, MetadataEntityFieldData>();
    for (const field of uiFields) {
      if (field.path) map.set(field.path, field);
      if (field.name) map.set(field.name, field);
    }
    const pick = (key: string | null | undefined) => (key ? map.get(String(key)) ?? null : null);
    const list = view?.list;
    const fieldIds = (list?.fields ?? []).map(String);
    const rightFields = orderListDisplayFields(fieldIds.map(id => map.get(id)).filter((f): f is MetadataEntityFieldData => !!f), store.$context()?.list);

    return {
      identifierField: pick(list?.identifier as string) ?? params.identifierField ?? null,
      titleField: pick(list?.title as string) ?? params.titleField ?? null,
      statusField: pick(list?.status as string) ?? params.statusField ?? null,
      // Prefer live list.fields; fall back to colDef only if store has no fields yet
      rightFields: rightFields.length ? rightFields : (params.rightFields ?? []),
    };
  }

  /** Assignee is its own always-visible slot; every other value becomes one ordered property. */
  private partitionRightFields(data: any, fields: (MetadataEntityFieldData | null | undefined)[]): {
    properties: RowProperty[];
    avatarContent: ContentModel | null;
    assignee: AssigneeSlot;
  } {

    if (!data || !fields?.length) {
      return { properties: [], avatarContent: null, assignee: { enabled: false, tooltip: null } };
    }

    const properties: RowProperty[] = [];
    const listConfig = this.store?.$context()?.list;
    const fixedProperties = listConfig?.fixedProperties ?? [];
    const pinnedIds = new Set(fixedProperties);
    const avatarTokens: ContentToken[] = [];
    let assignee: AssigneeSlot = { enabled: false, tooltip: null };

    for (const field of fields) {
      if (!field) continue;
      const id = String(field.path ?? field.name ?? '');
      const value = this.getValueFromPath(data, id);
      const fieldLabel = field.displayName ?? field.name ?? id;
      const pinned = pinnedIds.has(id);

      // A configured resolver owns this property. Null omits it, including raw false booleans; undefined falls through to the default rendering below.
      const presentation = listConfig?.propertyPresentations?.[id]?.({ item: data, field, injector: this.injector });
      if (presentation !== undefined) {
        if (presentation) properties.push(this.toPresentedProperty(id, field, presentation, pinned));
        continue;
      }

      const created = ListRowV2Renderer.CREATED_IDS.has(id);
      if (created || ListRowV2Renderer.UPDATED_IDS.has(id)) {
        const label = value != null && value !== '' ? this.formatCompactDate(value) : null;
        if (!label) continue;
        const name = created ? 'Created' : 'Updated';
        properties.push({ id, field, mode: BadgeMode.Pill, label, tooltip: `${name}: ${label}`, pinned, meta: true, foldRow: { label: name, value: label } });
        continue;
      }
      if (this.isPersonField(field)) {
        assignee = { enabled: true, tooltip: fieldLabel };
        if (value == null || value === '' || (Array.isArray(value) && !value.length)) {
          avatarTokens.push({ value: 'fa-regular fa-user', mode: AvatarMode.Icon });
          assignee.tooltip = `${fieldLabel}: Unassigned`;
          continue;
        }
        const items = Array.isArray(value) ? value : [value];
        const names: string[] = [];
        for (const item of items) {
          if (isUnallocatedUser(item)) {
            names.push(UnallocatedUserLabel);
            avatarTokens.push({ value: 'fa-regular fa-user', mode: AvatarMode.Icon });
            continue;
          }
          const name = typeof item === 'object' ? (item?.name ?? '') : String(item);
          if (name) {
            names.push(name);
            avatarTokens.push({ value: name, mode: AvatarMode.Letter });
          } else {
            avatarTokens.push({ value: 'fa-regular fa-user', mode: AvatarMode.Icon });
          }
        }
        assignee.tooltip = names.length ? `${fieldLabel}: ${names.join(', ')}` : `${fieldLabel}: Unassigned`;
        continue;
      }

      if (value == null || value === '' || (Array.isArray(value) && !value.length)) continue;
      const type = Array.isArray(field.type) ? field.type[0] : field.type;
      if (ListRowV2Renderer.DUE_IDS.has(id)) {
        const dueLabel = this.formatDueDate(value, id, data);
        if (!dueLabel) continue;
        const tone = this.dueDateStyle(value);
        properties.push({ id, field, mode: BadgeMode.Pill, label: dueLabel, tooltip: `${fieldLabel}: ${dueLabel}`, iconLeft: 'fa-regular fa-calendar', iconLeftColor: tone.color, color: tone.color, backgroundColor: tone.backgroundColor, borderColor: tone.borderColor, pinned, meta: false, foldRow: { label: fieldLabel, value: dueLabel } });
        continue;
      }
      if (type === MetadataEntityFieldType.Tag) {
        const summary = resolveListRowLabelSummary(value, fieldLabel);
        if (!summary) continue;
        properties.push({ id, field, mode: BadgeMode.Pill, ...summary, color: 'var(--local-list-row-property-text)', backgroundColor: 'var(--local-list-row-property-bg)', borderColor: 'var(--local-list-row-property-border)', pinned, meta: false, foldRow: this.foldRow(fieldLabel, summary.tooltip) });
        continue;
      }
      const label = this.formatBagValue(field, value, data);
      if (label) {
        properties.push({
          id,
          field,
          mode: BadgeMode.Pill,
          label,
          tooltip: `${fieldLabel}: ${label}`,
          color: 'var(--local-list-row-property-text)',
          backgroundColor: 'var(--local-list-row-property-bg)',
          borderColor: 'var(--local-list-row-property-border)',
          pinned,
          meta: false,
          foldRow: { label: fieldLabel, value: label },
        });
      }
    }

    const pinnedOrder = (item: RowProperty) => fixedProperties.indexOf(item.id);

    return {
      properties: [...properties.filter(item => !item.pinned), ...properties.filter(item => item.pinned).sort((a, b) => pinnedOrder(a) - pinnedOrder(b))],
      avatarContent: avatarTokens.length
        ? { label: null, avatar: avatarTokens, mode: 'item', labelMode: 'text', growLabel: false }
        : null,
      assignee,
    };
  }

  // A tooltip reads "Field: values"; the hover card already shows the field as the row label.
  private foldRow(fieldLabel: string, tooltip: string): HoverCardRow {

    const prefix = `${fieldLabel}: `;
    return { label: fieldLabel, value: tooltip.startsWith(prefix) ? tooltip.slice(prefix.length) : tooltip };
  }

  private toPresentedProperty(id: string, field: MetadataEntityFieldData, presentation: EntityBoardListPropertyPresentation, pinned: boolean): RowProperty {

    const { color, backgroundColor, borderColor } = listRowPropertyToneStyle(presentation.tone, presentation.disabled);
    const fieldLabel = field.displayName ?? field.name ?? id;

    return {
      id,
      field,
      mode: BadgeMode.Pill,
      label: presentation.label,
      tooltip: presentation.tooltip ?? presentation.label,
      iconLeft: presentation.iconLeft,
      iconLeftColor: presentation.iconLeftColor ?? color,
      color,
      backgroundColor,
      borderColor: presentation.borderless ? 'var(--local-list-row-property-borderless-border)' : borderColor,
      dashed: presentation.dashed,
      disabled: presentation.disabled,
      hoverCard: presentation.hoverCard,
      iconOnly: presentation.iconOnly,
      iconBadge: presentation.iconBadge,
      pinned,
      meta: false,
      foldRow: this.foldRow(fieldLabel, presentation.tooltip ?? presentation.label),
    };
  }

  private titleText(value: any): string | null {

    const text = typeof value === 'object' && value != null ? value.name ?? value.label : value;
    return text == null || text === '' ? null : String(text);
  }

  /** Shows every property for one render, records its width, then folds to fit. */
  private measure(): void {

    this.measureQueued = true;
    this.$folded.set(new Set());
    this.$measuring.set(true);
    afterNextRender({
      read: () => {
        this.measureQueued = false;
        const element = this.host.nativeElement;
        // A row rendered before AG attaches it has no width; widths read then are all zero, so wait for the resize.
        this.widthsStale = !element.clientWidth;
        if (this.widthsStale) return;
        this.foldWidths = Array.from(element.querySelectorAll<HTMLElement>('[data-fold-id]')).map(item => ({ id: item.dataset['foldId'] ?? '', width: item.offsetWidth }));
        this.pinnedWidth = element.querySelector<HTMLElement>('.list-row-pinned')?.offsetWidth ?? 0;
        this.layout();
      },
    }, { injector: this.injector });
  }

  private observeWidth(): void {

    if (typeof ResizeObserver === 'undefined') return;
    const observer = this.zone.runOutsideAngular(() => new ResizeObserver(() => this.scheduleLayout()));
    observer.observe(this.host.nativeElement);
    inject(DestroyRef).onDestroy(() => {
      observer.disconnect();
      if (this.layoutFrame != null) cancelAnimationFrame(this.layoutFrame);
    });
  }

  private scheduleLayout(): void {

    if (this.measureQueued || this.layoutFrame != null) return;
    this.layoutFrame = requestAnimationFrame(() => {
      this.layoutFrame = null;
      if (this.widthsStale) {
        this.zone.run(() => this.measure());
        return;
      }
      this.layout();
    });
  }

  private layout(): void {

    const element = this.host.nativeElement;
    const style = getComputedStyle(element);
    const gap = parseFloat(style.columnGap) || 0;
    const content = element.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    const checkbox = element.querySelector<HTMLElement>('.list-row-checkbox')?.offsetWidth ?? 0;
    const titleFloor = parseFloat(style.getPropertyValue('--local-list-row-title-floor')) || 0;
    const right = element.querySelector<HTMLElement>('.list-row-right');
    const rightGap = right ? parseFloat(getComputedStyle(right).columnGap) || 0 : 0;
    const budget = content - titleFloor - gap - (checkbox ? checkbox + gap : 0);
    const folded = foldListRowProperties(this.foldWidths, { budget, pinnedWidth: this.pinnedWidth, gap: rightGap, overflowWidth: OVERFLOW_PILL_WIDTH });

    this.zone.run(() => {
      if (!sameIds(this.$folded(), folded)) this.$folded.set(folded);
      this.$measuring.set(false);
      this.cdr.markForCheck();
    });
  }

  private formatCompactDate(value: any, type?: MetadataEntityFieldType, fieldName?: string, data?: { timezone?: unknown; invoiceDate?: unknown }): string | null {

    if (value == null || value === '') return null;
    const zone = fieldName
      ? timezoneForDisplayedDate(type, this.defaultTimezone, fieldName, data)
      : timezoneForFieldType(type, this.defaultTimezone);
    return this.dateTimePipe.transform(value, zone, 'MMM d', '') || null;
  }

  private formatDueDate(value: any, fieldName?: string, data?: { timezone?: unknown; invoiceDate?: unknown }): string | null {

    const zone = timezoneForDisplayedDate(MetadataEntityFieldType.Date, this.defaultTimezone, fieldName, data);
    try {
      const due = DateUtility.getDateTime(value, zone).startOf('day');
      const today = DateUtility.getDateTime(new Date(), zone).startOf('day');
      const days = Math.round(due.diff(today, 'days').days);
      if (days === 0) return 'Today';
      if (days === 1) return 'Tomorrow';
    } catch { /* Fall back to the compact absolute date. */ }
    return this.formatCompactDate(value, MetadataEntityFieldType.Date, fieldName, data);
  }

  private dueDateStyle(value: any): { color: string; backgroundColor: string; borderColor: string } {

    try {
      const due = DateUtility.getDateTime(value, this.defaultTimezone).startOf('day');
      const today = DateUtility.getDateTime(new Date(), this.defaultTimezone).startOf('day');
      const days = Math.floor(due.diff(today, 'days').days);
      if (days < 0) {
        return { color: 'var(--local-list-row-due-overdue-text)', backgroundColor: 'var(--local-list-row-due-overdue-bg)', borderColor: 'var(--local-list-row-due-overdue-border)' };
      }
      if (days <= 7) {
        return { color: 'var(--local-list-row-due-soon-text)', backgroundColor: 'var(--local-list-row-due-soon-bg)', borderColor: 'var(--local-list-row-due-soon-border)' };
      }
    } catch { /* Use the neutral future treatment for invalid dates. */ }
    return { color: 'var(--local-list-row-due-later-text)', backgroundColor: 'var(--local-list-row-due-later-bg)', borderColor: 'var(--local-list-row-due-later-border)' };
  }

  private formatBagValue(field: MetadataEntityFieldData, value: any, data?: { timezone?: unknown; invoiceDate?: unknown }): string {

    const type = Array.isArray(field.type) ? field.type[0] : field.type;
    if (type === MetadataEntityFieldType.Enum) {
      const options = (Array.isArray(field.typeData) ? field.typeData : []) as MetadataEnumTypeData[];
      if (Array.isArray(value)) return value.map(item => options.find(option => option.value === item)?.key ?? item).filter(Boolean).join(', ');
      return String(options.find(option => option.value === value)?.key ?? value ?? '');
    }
    if (type === MetadataEntityFieldType.Date || type === MetadataEntityFieldType.DateTime || type === MetadataEntityFieldType.Time) {
      return this.formatCompactDate(value, type, field.name ?? field.path, data) ?? '';
    }
    if (Array.isArray(value)) return value.map(item => isUnallocatedUser(item) ? UnallocatedUserLabel : (typeof item === 'object' ? item?.name || item?.friendlyName : item)).filter(Boolean).join(', ');
    if (typeof value === 'object') return isUnallocatedUser(value) ? UnallocatedUserLabel : String(value?.name || value?.friendlyName || value?.label || value?.displayName || '');
    if (type === MetadataEntityFieldType.Boolean) return value ? 'Yes' : 'No';
    return String(value ?? '');
  }

  private isPersonField(field: MetadataEntityFieldData): boolean {

    const type = Array.isArray(field.type) ? field.type[0] : field.type;
    if (type !== MetadataEntityFieldType.Lookup && type !== MetadataEntityFieldType.EmbeddedEntity && type !== MetadataEntityFieldType.Parent) return false;
    return ListRowV2Renderer.PERSON_ENTITIES.has(field.typeData?.type ?? field.typeData);
  }

  private getValueFromPath(item: any, path: string | null | undefined): any {

    if (!item || !path) return null;
    return path.split('.').reduce((acc, part) => acc?.[part], item);
  }

  private setNestedPresentation(item: any, titleField: MetadataEntityFieldData | null): void {

    const nested = this.store?.$context()?.list?.nested;
    const config: EntityBoardListNestedConfig | null = nested && typeof nested === 'object' ? nested : null;
    if (!config) {
      this.$nested.set(false);
      this.$nestedLast.set(true);
      this.$flatParentLabel.set(null);
      return;
    }

    const items = this.store?.$items() ?? [];
    const parent = getNestedListParentValue(item, items, config);
    if (!parent) {
      this.$nested.set(false);
      this.$nestedLast.set(true);
      this.$flatParentLabel.set(null);
      return;
    }

    const indent = this.store?.$effectiveView()?.list?.indentNestedItems ?? config.indentByDefault ?? false;
    this.$nested.set(indent && getNestedListItemDepth(item, items, config) > 0);
    this.$nestedLast.set(this.isLastNestedSibling(item, items, config));
    if (indent) {
      this.$flatParentLabel.set(null);
      return;
    }

    const titlePath = titleField?.path ?? titleField?.name ?? 'name';
    const value = this.getValueFromPath(parent, titlePath);
    const label = typeof value === 'object' && value != null ? value.name ?? value.label : value;
    this.$flatParentLabel.set(label == null || label === '' ? null : String(label));
  }

  /** Linear-style connectors: the trunk continues through every nested sibling except the last one. */
  private isLastNestedSibling(item: any, items: any[], config: EntityBoardListNestedConfig): boolean {

    const rowIndex = this.rowNode?.rowIndex;
    if (rowIndex == null || !this.gridApi) return true;
    const nextData = this.gridApi.getDisplayedRowAtIndex(rowIndex + 1)?.data;
    if (!nextData) return true;
    return getNestedListParentValue(nextData, items, config) !== getNestedListParentValue(item, items, config);
  }

  protected onSelectionToggle(selected: boolean): void {

    if (!this.$bulkSelectable() || !this.rowNode) return;
    this.rowNode.setSelected(selected);
    this.$selected.set(selected);
  }

  protected onMouseDown(event: MouseEvent): void {

    if (!this.$dragBlocked()) return;

    const rowIndex = this.rowNode?.rowIndex;
    if (rowIndex != null && this.columnId) this.gridApi?.setFocusedCell(rowIndex, this.columnId);
    event.stopPropagation();
  }
}
